import { forwardRef, type HTMLAttributes, type ButtonHTMLAttributes, type ReactNode } from 'react'
import { cx, ui, focus } from '../../lib/ui-primitives'

// ============================================
// CARD VARIANTS
// Glassmorphic card component from ui-primitives
// ============================================

export type CardVariant = 'default' | 'soft' | 'interactive' | 'selection'

const variantStyles: Record<CardVariant, string> = {
  default: ui.card,
  soft: ui.cardSoft,
  interactive: ui.cardInteractive,
  selection: ui.selectionCard,
}

const paddingStyles = {
  none: '',
  sm: 'p-3',
  md: 'p-4 sm:p-5',
  lg: 'p-5 sm:p-6',
}

interface CardBaseProps {
  /** Visual variant */
  variant?: CardVariant
  /** Padding size */
  padding?: 'none' | 'sm' | 'md' | 'lg'
  /** Custom accent color for gradient border effect */
  accentColor?: string
  /** Additional header content */
  header?: ReactNode
  /** Additional footer content */
  footer?: ReactNode
}

export interface CardProps extends CardBaseProps, HTMLAttributes<HTMLDivElement> {}

/**
 * Card - Glassmorphic container component
 *
 * Extracted from: All screens (ui.card, ui.cardSoft, ui.cardInteractive)
 *
 * Features:
 * - 4 variants for different contexts
 * - Optional accent color gradient
 * - Header/footer slots for compound layouts
 */
export const Card = forwardRef<HTMLDivElement, CardProps>(
  (
    {
      variant = 'default',
      padding = 'md',
      accentColor,
      header,
      footer,
      className,
      style,
      children,
      ...props
    },
    ref
  ) => {
    return (
      <div
        ref={ref}
        className={cx(
          variantStyles[variant],
          paddingStyles[padding],
          className
        )}
        style={{
          ...(accentColor && {
            background: `linear-gradient(135deg, ${accentColor}15, rgba(255,255,255,0.02) 55%, rgba(255,255,255,0.01))`,
          }),
          ...style,
        }}
        {...props}
      >
        {header && (
          <div className="border-b border-white/10 -mx-4 px-4 pb-3 mb-3 sm:-mx-5 sm:px-5">
            {header}
          </div>
        )}
        {children}
        {footer && (
          <div className="border-t border-white/10 -mx-4 px-4 pt-3 mt-3 sm:-mx-5 sm:px-5">
            {footer}
          </div>
        )}
      </div>
    )
  }
)

Card.displayName = 'Card'

// ============================================
// CARD BUTTON
// Interactive card as button
// ============================================

export interface CardButtonProps extends CardBaseProps, ButtonHTMLAttributes<HTMLButtonElement> {}

/**
 * CardButton - Interactive card as button element
 *
 * Use this for clickable cards that need button semantics.
 */
export const CardButton = forwardRef<HTMLButtonElement, CardButtonProps>(
  (
    {
      variant = 'interactive',
      padding = 'md',
      accentColor,
      header,
      footer,
      className,
      style,
      children,
      ...props
    },
    ref
  ) => {
    return (
      <button
        ref={ref}
        type="button"
        className={cx(
          variantStyles[variant],
          paddingStyles[padding],
          focus.ring,
          className
        )}
        style={{
          ...(accentColor && {
            background: `linear-gradient(135deg, ${accentColor}15, rgba(255,255,255,0.02) 55%, rgba(255,255,255,0.01))`,
          }),
          ...style,
        }}
        {...props}
      >
        {header && (
          <div className="border-b border-white/10 -mx-4 px-4 pb-3 mb-3 sm:-mx-5 sm:px-5">
            {header}
          </div>
        )}
        {children}
        {footer && (
          <div className="border-t border-white/10 -mx-4 px-4 pt-3 mt-3 sm:-mx-5 sm:px-5">
            {footer}
          </div>
        )}
      </button>
    )
  }
)

CardButton.displayName = 'CardButton'
